import { useCallback, useEffect, useRef, useState } from "react";
import "./Panelvis.css";

/**
 * Panel lateral derecho con el visor del documento embebido en un iframe.
 * Ing. Carlos F. Castaneda M.
 *
 * Se reutiliza la ruta /visor/:url para que el iframe conserve exactamente las
 * mismas acciones del visor en ventana aparte (zoom, rotar, buscar, miniaturas,
 * descargar e imprimir con los permisos del usuario). El aislamiento del iframe
 * ademas evita que los atajos de teclado y el bloqueo de clic derecho del visor
 * afecten al listado de tarjetas.
 */
// Minimo suficiente para que la barra del visor muestre a la vez el contador,
// el zoom y todos los iconos de permisos sin recortar ninguno.
const ANCHO_MIN = 460;
const ANCHO_DEF = 620;

const Panelvis = ({ show, titulo, src, onClose }) => {
  const [ancho, setAncho] = useState(ANCHO_DEF);
  const arrastreRef = useRef(null);

  const anchoMaximo = useCallback(() => Math.max(ANCHO_MIN, window.innerWidth - 320), []);

  /**
   * Reserva el espacio del panel para que las tarjetas no queden tapadas.
   * Se aplica en linea sobre el contenedor porque una regla de hoja de estilos
   * no le gana al padding que Bootstrap pone en .container-fluid.
   */
  useEffect(() => {
    const body = document.body;
    const contenedor = document.getElementById("div-components");
    const anchoPrevio = contenedor ? contenedor.style.paddingRight : "";
    const esAngosto = window.innerWidth <= 768;

    if (show) {
      body.classList.add("vp-abierto");
      if (contenedor) contenedor.style.paddingRight = esAngosto ? "" : `${ancho}px`;
    } else {
      body.classList.remove("vp-abierto");
      if (contenedor) contenedor.style.paddingRight = "";
    }
    return () => {
      body.classList.remove("vp-abierto");
      if (contenedor) contenedor.style.paddingRight = anchoPrevio;
    };
  }, [show, ancho]);

  // Cierra el panel con la tecla Escape
  useEffect(() => {
    if (!show) return undefined;
    const alPresionar = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", alPresionar);
    return () => document.removeEventListener("keydown", alPresionar);
  }, [show, onClose]);

  // Ajusta el ancho cuando cambia el tamaño de la ventana
  useEffect(() => {
    const alRedimensionar = () => setAncho((prev) => Math.min(prev, anchoMaximo()));
    window.addEventListener("resize", alRedimensionar);
    return () => window.removeEventListener("resize", alRedimensionar);
  }, [anchoMaximo]);

  /**
   * Arrastre del borde izquierdo para redimensionar el panel
   */
  useEffect(() => {
    const alMover = (e) => {
      if (!arrastreRef.current) return;
      const nuevo = arrastreRef.current.ancho + (arrastreRef.current.x - e.clientX);
      setAncho(Math.min(anchoMaximo(), Math.max(ANCHO_MIN, nuevo)));
    };
    const alSoltar = () => {
      if (!arrastreRef.current) return;
      arrastreRef.current = null;
      document.body.classList.remove("vp-arrastrando");
    };
    document.addEventListener("mousemove", alMover);
    document.addEventListener("mouseup", alSoltar);
    return () => {
      document.removeEventListener("mousemove", alMover);
      document.removeEventListener("mouseup", alSoltar);
    };
  }, [anchoMaximo]);

  const iniciarArrastre = (e) => {
    arrastreRef.current = { x: e.clientX, ancho };
    document.body.classList.add("vp-arrastrando");
  };

  if (!show) return null;

  return (
    <aside className="vp-panel" style={{ width: `${ancho}px` }}>
      <div
        className="vp-resize"
        onMouseDown={iniciarArrastre}
        title="Arrastrar para cambiar el ancho"
      />
      <div className="vp-hdr">
        <span className="vp-hdr__tipo" title={titulo}>
          {titulo}
        </span>
        <div className="vp-hdr__acc">
          <button
            type="button"
            className="vp-btn"
            onClick={() => window.open(src, "_blank")}
            title="Abrir en una ventana aparte"
          >
            <i className="bi bi-box-arrow-up-right"></i>
          </button>
          <button type="button" className="vp-btn vp-btn--x" onClick={onClose} title="Cerrar visor">
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
      </div>
      <iframe
        key={src}
        className="vp-frame"
        src={src}
        title={titulo || "Visor del documento"}
      />
    </aside>
  );
};

export default Panelvis;
